import { useMemo, useState } from 'react';
import { EditOutlined } from '@ant-design/icons';
import { App, Button, Dropdown, Space, Spin } from 'antd';
import { useIntl } from 'react-intl';

import BadgeDisplay from '@/pages/drama-review/components/badge-display';
import { BADGE_OPTIONS, badgeOptionIntlId, normalizeBadge, type DramaBadge } from '@/pages/drama-review/utils';

type Props = {
    value?: DramaBadge | string | null;
    editable?: boolean;
    loading?: boolean;
    onConfirm: (badge: DramaBadge) => void | Promise<void>;
};

const BadgeCell: React.FC<Props> = ({ value, editable, loading, onConfirm }) => {
    const { formatMessage } = useIntl();
    const { modal } = App.useApp();
    const [open, setOpen] = useState(false);
    const normalizedValue = normalizeBadge(value);

    const menuItems = useMemo(
        () =>
            BADGE_OPTIONS.map((badge) => ({
                key: badge,
                disabled: loading || badge === normalizedValue,
                label: formatMessage({ id: badgeOptionIntlId(badge) }),
            })),
        [formatMessage, loading, normalizedValue],
    );

    const handleMenuClick = ({ key }: { key: string }) => {
        const badge = key as DramaBadge;
        if (!BADGE_OPTIONS.includes(badge) || badge === normalizedValue) return;

        setOpen(false);
        modal.confirm({
            title: formatMessage({ id: 'dramaReview.badge.confirm' }),
            content: formatMessage({ id: badgeOptionIntlId(badge) }),
            onOk: () => onConfirm(badge),
        });
    };

    return (
        <Space size={6} align="center">
            <BadgeDisplay value={value} />
            {editable ? (
                loading ? (
                    <Spin size="small" />
                ) : (
                    <Dropdown
                        open={open}
                        trigger={['click']}
                        menu={{
                            items: menuItems,
                            onClick: handleMenuClick,
                        }}
                        onOpenChange={setOpen}
                    >
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            style={{ padding: 0, height: 'auto' }}
                        />
                    </Dropdown>
                )
            ) : null}
        </Space>
    );
};

export default BadgeCell;
